import * as classNames from 'classnames';
import { UserType } from 'common/interfaces/user';
import { IconButton, IIconButtonProps } from 'components/action';
import { ActivitiesGroup, ActivityCreateOrEdit } from 'components/activity';
import { SectionHeadline } from 'components/content';
import { LoadingFullScreen } from 'components/indicator';
import { DropdownContainer, OptionsList } from 'components/list';
import { computed } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { IAppOnlyStore } from 'stores/AllStores';

// Styles
const s = require('./LeadActivities.css');
const cancelIcon = require('assets/close orange icon.svg');
const addIcon = require('assets/plus icon.svg');

// Pages
@inject('store')
@observer
export class LeadActivities extends React.Component<IAppOnlyStore, {}> {
  constructor(props: any) {
    super(props);
  }

  @computed private get activitiesOfUserFilterOptions(): Array<{
    value: any;
    label: string;
  }> {
    const { user, lead } = this.props.store;

    let filterUsers: Array<{ value: number; label: string }> = [];

    filterUsers.push({ value: user.id, label: user.name || user.username });

    if (user.userType === UserType.SalesManager) {
      lead.openLeadDetails.activitiesAssignedToUsers.forEach(
        (assignedToUser) => {
          if (assignedToUser.id !== user.id) {
            filterUsers.push({
              value: assignedToUser.id,
              label: assignedToUser.name || assignedToUser.username,
            });
          }
        }
      );
    }

    filterUsers = filterUsers.sort((a, b) => {
      const aName = a.label ? a.label.toLowerCase() : '';
      const bName = b.label ? b.label.toLowerCase() : '';

      return aName > bName ? 1 : aName < bName ? -1 : 0;
    });

    return filterUsers;
  }

  @computed private get activitiesActionButtonProps(): IIconButtonProps {
    const { activitiesCreateOrEdit } = this.props.store;

    return {
      onClick: () => {
        if (activitiesCreateOrEdit.newActivity !== null) {
          activitiesCreateOrEdit.resetNewActivity();
        } else {
          activitiesCreateOrEdit.initializeNewActivity(
            this.props.store.lead.openLeadId
          );
        }
      },
      label:
        activitiesCreateOrEdit.newActivity !== null
          ? 'Cancel creating'
          : 'New activity',
      icon: activitiesCreateOrEdit.newActivity !== null ? cancelIcon : addIcon,
      className: s.cancelOrAddButton,
      labelOnRight: true,
      showLabel: true,
    };
  }

  @computed private get showDefineNewActivityButton(): boolean {
    return (
      this.props.store.leadActivities.leadActivitiesLoading === false &&
      this.props.store.activitiesCreateOrEdit.newActivity === null
    );
  }

  public render() {
    const { user, leadActivities, activitiesCreateOrEdit } = this.props.store;

    return (
      <div className={s.leadActivities}>
        <SectionHeadline
          className={
            leadActivities.openLeadAllActivitiesCount <= 0
              ? s.noActivitiesHeadline
              : s.activitiesHeadline
          }
          title="Activities"
          details={
            leadActivities.openLeadAllActivitiesCount <= 0
              ? 'No activities assigned.'
              : undefined
          }
          button={
            this.showDefineNewActivityButton
              ? {
                  onClick: () => {
                    activitiesCreateOrEdit.initializeNewActivity(
                      this.props.store.lead.openLeadId
                    );
                  },
                  icon: addIcon,
                  label: 'Define new activity',
                  showLabel: true,
                  labelOnRight: false,
                }
              : null
          }
        />

        <div
          className={classNames(s.activitiesFilters, {
            [s.paddingRight]: this.showDefineNewActivityButton,
          })}
        >
          {user.userType === UserType.SalesManager && (
            <DropdownContainer
              label="Activities of"
              ref={this.referenceActivitiesUserFilter}
              value={
                leadActivities.leadActivitiesForSalesPerson.salesPersonName
              }
            >
              <OptionsList
                label="Filter by person"
                onChange={this.handleActivitiesOfUserFilterChange}
                value={
                  leadActivities.leadActivitiesForSalesPerson
                    ? leadActivities.leadActivitiesForSalesPerson.salesPersonId
                    : -1
                }
                options={[
                  {
                    value: -1,
                    label: 'All',
                  },
                ].concat(this.activitiesOfUserFilterOptions)}
              />
            </DropdownContainer>
          )}

          <DropdownContainer
            label="Show"
            ref={this.referenceActivitiesFilter}
            value={
              leadActivities.showCompletedActivities
                ? (user.maxFinishedActivitiesCount > 0 &&
                  user.maxFinishedActivitiesCount <= 100
                    ? user.maxFinishedActivitiesCount
                    : 'all') + ' completed activities'
                : 'only open activities'
            }
          >
            <OptionsList
              label="Activities filter"
              onChange={this.handleActivitiesFilterChange}
              value={
                leadActivities.showCompletedActivities
                  ? user.maxFinishedActivitiesCount
                  : 0
              }
              options={[
                { value: 0, label: 'Only open activities' },
                {
                  value: 10,
                  label: 'Last 10 completed activities',
                },
                {
                  value: 25,
                  label: 'Last 25 completed activities',
                },
                {
                  value: 50,
                  label: 'Last 50 completed activities',
                },
                {
                  value: 100,
                  label: 'Last 100 completed activities',
                },
                {
                  value: 99999999,
                  label: 'All completed activities',
                },
              ]}
            />
          </DropdownContainer>
        </div>

        {leadActivities.openLeadAllActivitiesCount >= 0
          ? this.renderActivitiesListByGroup()
          : null}

        {activitiesCreateOrEdit.newActivity !== null && (
          <div className={s.leadDefineActivity}>
            <div className={s.title}>
              <h4>Define</h4>
            </div>
            <div className={s.create}>
              <ActivityCreateOrEdit />
            </div>
          </div>
        )}

        {(leadActivities.openLeadAllActivitiesCount > 0 ||
          activitiesCreateOrEdit.newActivity !== null) && (
          <IconButton {...this.activitiesActionButtonProps} />
        )}

        <LoadingFullScreen
          active={leadActivities.leadActivitiesLoading}
          fitInContainer={true}
        />
      </div>
    );
  }

  private renderActivitiesListByGroup = () => {
    return this.props.store.leadActivities.activitiesGroups.map((group) => {
      return group.activities.length > 0 ? (
        <ActivitiesGroup
          key={group.key}
          title={group.title}
          allFinished={group.allFinished}
          activities={group.activities}
          isOnLeadPage={true}
        />
      ) : null;
    });
  };

  private activitiesUserFilterDropdown: DropdownContainer = null;
  private referenceActivitiesUserFilter = (ref: DropdownContainer) =>
    (this.activitiesUserFilterDropdown = ref);

  private activitiesFilterDropdown: DropdownContainer = null;
  private referenceActivitiesFilter = (ref: DropdownContainer) =>
    (this.activitiesFilterDropdown = ref);

  private handleActivitiesOfUserFilterChange = (changed: {
    value: number;
    label: string;
  }) => {
    this.props.store.leadActivities.setLeadActivitiesForSalesPerson(
      this.props.store.lead.openLeadId,
      changed.value,
      changed.label
    );

    if (this.activitiesUserFilterDropdown) {
      this.activitiesUserFilterDropdown.closeDropdown();
    }
  };

  private handleActivitiesFilterChange = (changed: {
    value: number;
    label: string;
  }) => {
    if (changed.value <= 0) {
      this.props.store.leadActivities.showCompletedActivities = false;
    } else {
      this.props.store.user.changeMaxActivitiesPreference(changed.value);
      this.props.store.leadActivities.showCompletedActivities = true;
    }

    if (this.activitiesFilterDropdown) {
      this.activitiesFilterDropdown.closeDropdown();
    }
  };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/routes/sales/Lead/Activities/LeadActivities.tsx